import React from 'react'
import classNames from 'classnames'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { EyebrowMD, Footer, TitleSM, TitleXXS } from '@/atoms/Text'
import { ValidNoFeesDiscount } from '@/services/Theatrical/queries'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { useRestrictionCopy } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'

interface NoFeesBannerProps {
  code: string
  backgroundImage: PromoImage
  foregroundImage: PromoImage
  accentColor: string
  isDarkMode: boolean
  noFeesDiscount: ValidNoFeesDiscount
  className?: string
}

export const NoFeesBanner: React.FC<NoFeesBannerProps> = ({
  code,
  backgroundImage,
  foregroundImage,
  accentColor,
  isDarkMode,
  noFeesDiscount,
  className,
}) => {
  const { t } = useTranslate('tickets')
  const restrictionCopy = useRestrictionCopy(noFeesDiscount)
  const textColor = isDarkMode ? 'core-gray-100' : 'core-gray-950'

  return (
    <div className={className}>
      <div className="relative z-[39] h-[140px] rounded-xl @container">
        <div className="absolute h-[140px] w-full overflow-hidden rounded-xl">
          <div className="absolute inset-0 z-0" />
          {/* Main background image */}
          <Image
            src={backgroundImage.url}
            className="z-10"
            fill={true}
            style={{ objectFit: 'cover', objectPosition: '50% 50%' }}
            alt="No Fees Background"
            quality={50}
          />
          {/* Main image */}
          <Image
            src={foregroundImage.url}
            className="absolute right-0 top-0 z-[11] hidden h-[140px] rounded-r-xl @xl:block"
            height={140}
            width={(foregroundImage.width / foregroundImage.height) * 140}
            alt="No Fees Foreground"
          />
        </div>
        <div className="absolute left-0 top-0 z-20 flex h-full w-full items-center justify-around gap-4 px-3 @xl:justify-start @xl:gap-6 @2xl:gap-[10%] @2xl:px-6 @4xl:justify-start">
          <FlexColumn className="flex flex-nowrap justify-center">
            <TitleSM
              color={textColor}
              className="relative !leading-[1.1] @lg:!text-[1.5rem] @5xl:!text-[1.875rem]"
              weight="bold"
            >
              <Translate i18nKey="noOnlineFeesNoHassle" t={t}>
                No Online Fees. <span className="block @5xl:inline">No Hassle.</span>
              </Translate>
            </TitleSM>
            <div className="z-[70] pt-1">
              {restrictionCopy && (
                <SpecificTimesToolTip
                  buttonClassName="!px-0 whitespace-nowrap"
                  buttonColor={isDarkMode ? 'white' : 'black'}
                  copy={restrictionCopy}
                >
                  <TitleXXS color={textColor} weight="semibold">
                    {t('onSelectShowtimes', 'On Select Showtimes')}
                  </TitleXXS>
                </SpecificTimesToolTip>
              )}
              <FlexibleBookingToolTip
                className="mt-2"
                buttonColor={isDarkMode ? 'core-gray-100' : 'core-gray-950'}
                buttonClassName={classNames(
                  'focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter whitespace-nowrap',
                  isDarkMode ? 'bg-white/10 text-core-gray-100' : 'bg-black/10 text-core-gray-950',
                )}
              />
            </div>
          </FlexColumn>
          <FlexColumn className="flex flex-nowrap items-start justify-center">
            <FlexColumn className={'flex items-center gap-1 text-center'}>
              <EyebrowMD color={textColor} className="whitespace-nowrap" weight="semibold">
                {t('usePromoCode', 'Use Promo Code')}
              </EyebrowMD>
              <div style={{ backgroundColor: accentColor }} className="my-1 px-2 py-1">
                <TitleSM color="white" weight="bold">
                  {code}
                </TitleSM>
              </div>
              <EyebrowMD color={textColor} weight={'semibold'}>
                {t('atCheckout', 'At Checkout')}
              </EyebrowMD>
              <Footer italic color={textColor} className="block @lg:whitespace-nowrap">
                {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
              </Footer>
            </FlexColumn>
          </FlexColumn>
        </div>
      </div>
    </div>
  )
}
